import { useState } from "react";
import { BarChart3, Download, Landmark, TriangleAlert } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatApiError } from "@/lib/api";

const initialForm = {
  client_bill_rate: "125",
  expected_provider_pay: "65",
  payroll_taxes: "5500",
  workers_comp: "1200",
  insurance: "1800",
  benefits: "6000",
  travel: "1200",
  recruiting_cost: "3500",
  administrative_overhead: "4200",
  software_allocation: "900",
  expected_weekly_hours: "40",
  contract_weeks: "40",
  target_margin_percent: "30",
};

const fields = [
  ["client_bill_rate", "Client bill rate / hour"],
  ["expected_provider_pay", "Expected provider pay / hour"],
  ["payroll_taxes", "Payroll taxes · contract total"],
  ["workers_comp", "Workers comp · contract total"],
  ["insurance", "Insurance · contract total"],
  ["benefits", "Benefits · contract total"],
  ["travel", "Travel · contract total"],
  ["recruiting_cost", "Recruiting cost · contract total"],
  ["administrative_overhead", "Administrative overhead · contract total"],
  ["software_allocation", "Software allocation · contract total"],
  ["expected_weekly_hours", "Expected weekly hours"],
  ["contract_weeks", "Contract length · weeks"],
  ["target_margin_percent", "Target net margin %"],
];

function currency(value) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);
}

function MarginMetric({ label, value, testId }) {
  return (
    <article data-testid={testId}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

export const MarginAnalyzerPanel = () => {
  const [form, setForm] = useState(initialForm);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  function payload() {
    return Object.fromEntries(Object.entries(form).map(([key, value]) => [key, Number(value)]));
  }

  async function calculate(event) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await api.post("/margin-analyzer/calculate", payload());
      setAnalysis(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function exportPdf() {
    try {
      const response = await api.post("/margin-analyzer/export", payload(), { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "tagforce-margin-analysis.pdf";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <section className="margin-analyzer-page" data-testid="margin-analyzer-page">
      <section className="section-heading margin-intro">
        <div>
          <h2>Contract margin analyzer</h2>
          <p>Model delivery economics before TAG accepts a client commitment.</p>
        </div>
        {analysis && (
          <button className="button button-secondary" data-testid="export-margin-analysis-button" type="button" onClick={exportPdf}>
            <Download size={16} /> Export PDF
          </button>
        )}
      </section>
      {error && <p className="form-message error-message" data-testid="margin-analyzer-error">{error}</p>}
      <section className="margin-layout">
        <form className="margin-input-panel" data-testid="margin-analysis-form" onSubmit={calculate}>
          <div className="margin-panel-heading">
            <Landmark size={18} />
            <div>
              <h2>Contract assumptions</h2>
              <p>Enter total non-payroll costs for the full contract term.</p>
            </div>
          </div>
          <div className="margin-form-grid">
            {fields.map(([key, label]) => (
              <label key={key}>
                {label}
                <input data-testid={`margin-${key}-input`} min="0" required step="0.01" type="number" value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })} />
              </label>
            ))}
          </div>
          <button className="button button-primary" data-testid="calculate-margin-button" disabled={isSubmitting}>
            <BarChart3 size={16} />
            {isSubmitting ? "Calculating…" : "Calculate margin"}
          </button>
        </form>
        <section className="margin-results-panel" data-testid="margin-results-panel">
          {!analysis && (
            <p className="operations-empty-state" data-testid="margin-results-empty-state">
              Run a calculation to review gross margin, net margin, break-even price, and contract profit.
            </p>
          )}
          {analysis && (
            <>
              <div className="margin-result-heading">
                <div>
                  <p className="eyebrow">ECONOMIC OUTLOOK</p>
                  <h2>Contract result</h2>
                </div>
                {analysis.below_target && (
                  <span className="margin-target-alert" data-testid="margin-below-target-alert">
                    <TriangleAlert size={15} /> Below target
                  </span>
                )}
              </div>
              <div className="margin-kpi-grid">
                <MarginMetric label="Net margin" value={`${analysis.net_margin_percent}%`} testId="margin-net-percent" />
                <MarginMetric label="Gross margin" value={`${analysis.gross_margin_percent}%`} testId="margin-gross-percent" />
                <MarginMetric label="Profit / week" value={currency(analysis.profit_per_week)} testId="margin-weekly-profit" />
                <MarginMetric label="Contract profit" value={currency(analysis.profit_for_contract)} testId="margin-contract-profit" />
                <MarginMetric label="Break-even rate" value={`${currency(analysis.break_even_bill_rate)}/hr`} testId="margin-break-even-rate" />
                <MarginMetric label="Contract hours" value={analysis.annual_hours.toLocaleString()} testId="margin-contract-hours" />
              </div>
              <div className="margin-chart" data-testid="margin-analysis-chart">
                <ResponsiveContainer height="100%" width="100%">
                  <BarChart data={analysis.chart_data}>
                    <CartesianGrid stroke="#27606a" strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fill: "#b9d1d4", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#b9d1d4", fontSize: 10 }} />
                    <Tooltip formatter={(value) => currency(value)} />
                    <Bar dataKey="value" fill="#d4af37" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="margin-cost-list" data-testid="margin-cost-breakdown">
                {analysis.cost_breakdown.map((item) => (
                  <p key={item.label}>
                    <span>
                      {item.label}
                      <em>{item.category}</em>
                    </span>
                    <b>{currency(item.value)}</b>
                  </p>
                ))}
              </div>
            </>
          )}
        </section>
      </section>
    </section>
  );
};
